import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Palette, Upload, Save, Image, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

const LogoUploader = ({ label, currentLogo, onUpload, testId }) => {
  const ref = useRef();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Máximo 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => onUpload(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="flex items-center gap-4">
        <div
          className="w-40 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => ref.current?.click()}
        >
          {currentLogo ? (
            <img src={currentLogo} alt={label} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-center text-slate-400">
              <Image size={24} className="mx-auto mb-1" />
              <span className="text-[10px]">Click para subir</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Button size="sm" variant="outline" onClick={() => ref.current?.click()} className="text-xs">
            <Upload size={12} className="mr-1" /> Subir imagen
          </Button>
          {currentLogo && (
            <Button size="sm" variant="ghost" onClick={() => onUpload(null)} className="text-xs text-red-500">
              Quitar
            </Button>
          )}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleFile} />
      <p className="text-[10px] text-slate-400">PNG, JPG, SVG o WebP. Máx 2MB.</p>
    </div>
  );
};

const ColorPicker = ({ label, value, onChange, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#1e40af'}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded cursor-pointer border border-slate-200"
      />
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#1e40af"
        className="w-28 text-xs font-mono"
      />
    </div>
  </div>
);

export default function BrandingSettings() {
  const [config, setConfig] = useState({
    nombre_empresa: 'AlertGuard',
    logo_login: null,
    logo_header: null,
    logo_informe: null,
    color_primario: '#1e40af',
    color_secundario: '#334155',
    color_acento: '#0ea5e9',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/branding').then(r => {
      if (r.data && r.data.nombre_empresa) setConfig(r.data);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nombre_empresa: config.nombre_empresa,
        color_primario: config.color_primario,
        color_secundario: config.color_secundario,
        color_acento: config.color_acento,
        logo_login_base64: config.logo_login,
        logo_header_base64: config.logo_header,
        logo_informe_base64: config.logo_informe,
      };
      const res = await api.put('/branding', payload);
      setConfig(res.data);
      toast.success('Branding actualizado correctamente');
    } catch (e) {
      toast.error('Error al guardar el branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="branding-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Palette size={24} className="text-blue-600" /> Branding
          </h1>
          <p className="text-sm text-slate-500 mt-1">Personaliza la apariencia de la plataforma y los informes</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
          <Save size={16} className="mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* Nombre de empresa */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Identidad</h3>
        <div className="space-y-1">
          <Label className="text-sm font-medium text-slate-700">Nombre de la empresa</Label>
          <Input
            value={config.nombre_empresa}
            onChange={(e) => setConfig(p => ({ ...p, nombre_empresa: e.target.value }))}
            placeholder="AlertGuard"
            className="max-w-sm"
            data-testid="nombre-empresa-input"
          />
        </div>
      </Card>

      {/* Logos */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Logos</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <LogoUploader
            label="Logo de Login"
            currentLogo={config.logo_login}
            onUpload={(v) => setConfig(p => ({ ...p, logo_login: v }))}
            testId="logo-login-uploader"
          />
          <LogoUploader
            label="Logo Header (Barra superior)"
            currentLogo={config.logo_header}
            onUpload={(v) => setConfig(p => ({ ...p, logo_header: v }))}
            testId="logo-header-uploader"
          />
          <LogoUploader
            label="Logo para Informes"
            currentLogo={config.logo_informe}
            onUpload={(v) => setConfig(p => ({ ...p, logo_informe: v }))}
            testId="logo-informe-uploader"
          />
        </div>
      </Card>

      {/* Colores */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Colores</h3>
        <div className="grid grid-cols-3 gap-6">
          <ColorPicker
            label="Color Primario"
            value={config.color_primario}
            onChange={(v) => setConfig(p => ({ ...p, color_primario: v }))}
            testId="color-primario-picker"
          />
          <ColorPicker
            label="Color Secundario"
            value={config.color_secundario}
            onChange={(v) => setConfig(p => ({ ...p, color_secundario: v }))}
            testId="color-secundario-picker"
          />
          <ColorPicker
            label="Color Acento"
            value={config.color_acento}
            onChange={(v) => setConfig(p => ({ ...p, color_acento: v }))}
            testId="color-acento-picker"
          />
        </div>

        {/* Preview */}
        <div className="mt-6 pt-4 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-1"><Eye size={12} /> Vista previa</p>
          <div className="flex items-center gap-4">
            <div className="rounded-lg p-3" style={{ backgroundColor: config.color_primario }}>
              <span className="text-white text-sm font-semibold">Primario</span>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: config.color_secundario }}>
              <span className="text-white text-sm font-semibold">Secundario</span>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: config.color_acento }}>
              <span className="text-white text-sm font-semibold">Acento</span>
            </div>
            <div className="rounded-lg p-3 border border-slate-200 bg-white flex items-center gap-2">
              {config.logo_header && <img src={config.logo_header} alt="Logo" className="h-6" />}
              <span className="font-bold" style={{ color: config.color_primario }}>{config.nombre_empresa}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
